/**
 * Domain types.
 *
 * Every date is stored as an ISO 8601 string rather than a Firestore
 * Timestamp. That keeps documents portable: an export is the raw document,
 * and the same shapes work in a future non-Firebase client.
 */

/**
 * What is physically in the case.
 *
 * - `4k`    — a single UHD disc
 * - `hd`    — a single 1080p Blu-ray disc
 * - `combo` — one case, two discs: UHD *and* Blu-ray
 */
export type DiscFormat = '4k' | 'hd' | 'combo';

export const DISC_FORMATS: readonly DiscFormat[] = ['4k', 'hd', 'combo'] as const;

/** Number of physical discs a copy of this format contains. */
export const DISCS_PER_FORMAT: Record<DiscFormat, number> = { '4k': 1, hd: 1, combo: 2 };

export interface Loan {
  /** Who has it. Free text — this is a shelf, not a library system. */
  to: string;
  /** ISO date (yyyy-mm-dd) the copy left the shelf. */
  since: string;
  note?: string | null;
}

/** One physical case on the shelf. A movie can have several. */
export interface Copy {
  id: string;
  format: DiscFormat;
  /** Release variant: "Steelbook", "Mediabook", "Director's Cut", … */
  edition?: string | null;
  notes?: string | null;
  /** ISO date (yyyy-mm-dd). */
  purchasedAt?: string | null;
  /** Present only while the copy is lent out. */
  loan?: Loan | null;
  /**
   * Which shelf this disc is on. Absent means the default location — which is
   * every copy added before locations existed, so nothing needs migrating.
   */
  locationId?: string | null;
}

export interface Movie {
  id: string;
  tmdbId: number | null;
  /** English title — the display title when the UI is in English. */
  title: string;
  /** German title from TMDB, when it differs. */
  titleDe?: string | null;
  originalTitle?: string | null;
  year: number | null;
  /** Exact release date from TMDB, `yyyy-mm-dd`. Null when unknown. */
  releaseDate: string | null;
  /** Minutes. Drives every watch-time statistic. */
  runtime: number | null;
  /** TMDB poster path, e.g. `/wZv2Xj.jpg`. Not a full URL. */
  posterPath: string | null;
  backdropPath: string | null;
  overviewEn?: string | null;
  overviewDe?: string | null;
  /** TMDB genre ids — localised at render time, so the data stays language-neutral. */
  genreIds: number[];
  directors: string[];
  copies: Copy[];
  personalNote?: string | null;
  addedAt: string;
  updatedAt: string;
  /** Denormalised from the watch log so the grid can sort without loading it. */
  watchCount: number;
  lastWatchedAt: string | null;
}

export interface Watch {
  id: string;
  movieId: string;
  /** ISO datetime. */
  watchedAt: string;
  /** 1–5, or null when not rated. */
  rating?: number | null;
  note?: string | null;
  /**
   * Runtime snapshot taken when the watch was logged, so historical watch
   * time stays stable if TMDB later corrects the runtime.
   */
  runtimeMinutes: number | null;
  createdAt: string;
}

export interface WishlistItem {
  id: string;
  tmdbId: number | null;
  title: string;
  titleDe?: string | null;
  year: number | null;
  posterPath: string | null;
  releaseDate?: string | null;
  runtime: number | null;
  /** Which edition you're holding out for. */
  desiredFormat?: DiscFormat | null;
  /** TMDB genre ids, so the wishlist filters the same way the shelf does. */
  genreIds: number[];
  /**
   * Whether you could actually buy it today. False for a film still in
   * cinemas, or not released at all — the list is worth keeping either way,
   * but only one half of it is worth acting on.
   */
  buyable: boolean;
  /**
   * Set when the entry is in the trash. It stays readable for
   * TRASH_RETENTION_DAYS so a mis-tap can be undone, then it is purged.
   */
  deletedAt?: string | null;
  note?: string | null;
  addedAt: string;
}

/** A movie plus the watches that belong to it. */
export interface MovieWithWatches {
  movie: Movie;
  watches: Watch[];
}
